import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { GROUP_HEADER_CELL_BORDER_BOTTOM, PINNED_RIGHT_EDGE_LINE_STYLE } from '../../../../constants/borderStyles';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import { isEagColumnGroup } from '../../../../utils/isEagColumnGroup';
import { findFillerInsertIndex } from './utils/findFillerInsertIndex';
export type GroupHeaderRowProps = {
  columnConfig: EagColumnOrGroup[];
  visibleKeys: Set<string>;
  // When action column is enabled, add a matching empty <th> for structure
  actionColumnWidth?: number;
  // Number of non-right-pinned visible columns — used for filler <th> positioning
  nonRightPinnedCount: number;
};
export type RenderCell = {
  type: 'group';
  groupId: string;
  headerName: string;
  colSpan: number;
  positionKey: string;
} | {
  type: 'ungrouped';
  columnKey: string;
};

/**
 * Renders the group header <tr> that sits above the column header row.
 * Each EagColumnGroup becomes a <th colSpan={n}> where n is the number
 * of its children that are currently visible. Ungrouped columns get an
 * empty placeholder <th colSpan={1}>. Groups whose children are all
 * hidden are omitted entirely.
 *
 * The same groupId may appear multiple times in columnConfig when group members
 * are non-consecutive in the definition. Each occurrence is tracked with a
 * positional key (groupId + occurrence index) to avoid duplicate React keys.
 */
export function GroupHeaderRow(props) {
  const $ = _c(14);
  let t0;
  let t1;
  let t2;
  if ($[0] !== props.columnConfig || $[1] !== props.nonRightPinnedCount || $[2] !== props.visibleKeys) {
    const groupOccurrences = new Map();
    const renderCells = [];
    for (const item of props.columnConfig) {
      if (isEagColumnGroup(item)) {
        const visibleCount = item.children.filter(child => props.visibleKeys.has(child.key)).length;
        if (visibleCount === 0) {
          continue;
        }
        const occurrence = (groupOccurrences.get(item.groupId) ?? 0) + 1;
        groupOccurrences.set(item.groupId, occurrence);
        renderCells.push({
          type: "group",
          groupId: item.groupId,
          headerName: item.headerName,
          colSpan: visibleCount,
          positionKey: `${item.groupId}-${occurrence}`
        });
      } else {
        if (!props.visibleKeys.has(item.key)) {
          continue;
        }
        renderCells.push({
          type: "ungrouped",
          columnKey: item.key
        });
      }
    }
    const fillerInsertIndex = findFillerInsertIndex(renderCells, props.nonRightPinnedCount);
    const beforeFiller = renderCells.slice(0, fillerInsertIndex);
    const afterFiller = renderCells.slice(fillerInsertIndex);
    const renderCell = _temp;
    t0 = beforeFiller.map(renderCell);
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <th aria-hidden={true} style={{
        position: "sticky",
        top: 0,
        background: "white",
        ...GROUP_HEADER_CELL_BORDER_BOTTOM
      }} />;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    t2 = afterFiller.map(renderCell);
    $[0] = props.columnConfig;
    $[1] = props.nonRightPinnedCount;
    $[2] = props.visibleKeys;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[7] !== props.actionColumnWidth) {
    t3 = props.actionColumnWidth ? <th aria-hidden={true} className="p-0 border-0 bg-gray-50" style={{
      position: "sticky",
      top: 0,
      right: 0,
      zIndex: "var(--z-content-loader)",
      width: props.actionColumnWidth,
      maxWidth: props.actionColumnWidth,
      ...GROUP_HEADER_CELL_BORDER_BOTTOM
    }}><span style={PINNED_RIGHT_EDGE_LINE_STYLE} /></th> : null;
    $[7] = props.actionColumnWidth;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <tr>{t0}{t1}{t2}{t3}</tr>;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp(cell) {
  if (cell.type === "group") {
    return <th key={cell.positionKey} colSpan={cell.colSpan} className="text-xs font-semibold text-gray-500 text-center bg-gray-50 truncate px-2 py-1" style={{
      position: "sticky",
      top: 0,
      zIndex: "var(--z-content-loader)",
      ...GROUP_HEADER_CELL_BORDER_BOTTOM
    }}>{cell.headerName}</th>;
  }
  return <th key={cell.columnKey} colSpan={1} className="bg-gray-50" style={{
    position: "sticky",
    top: 0,
    zIndex: "var(--z-content-loader)",
    ...GROUP_HEADER_CELL_BORDER_BOTTOM
  }} />;
}